import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { logger } from '@utils/safeLogger'

import '../components/pages/FormBuilder/Shared/FormPdfKioskHeader/FormPdfKioskHeader.css'

export interface FormPdfClinicHeaderPayload {
  clinicName: string
  clinicAddress?: string
}

const FORM_PDF_LOGO_PATH = '/logo512.png'
const FORM_PDF_HEADER_CAPTURE_EXTRA_HEIGHT = 96

function resolveFormPdfLogoUrl(): string {
  if (typeof window === 'undefined') {
    return FORM_PDF_LOGO_PATH
  }
  return new URL(FORM_PDF_LOGO_PATH, window.location.origin).href
}

async function loadFormPdfLogoAsDataUrl(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const url = resolveFormPdfLogoUrl()
    const res = await fetch(url, { cache: 'force-cache' })
    if (!res.ok) {
      return null
    }
    const blob = await res.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = (): void => {
        const r = reader.result
        resolve(typeof r === 'string' ? r : '')
      }
      reader.onerror = (): void => {
        reject(reader.error ?? new Error('FileReader failed'))
      }
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

async function resolveFormPdfLogoSrcForCapture(): Promise<string> {
  const dataUrl = await loadFormPdfLogoAsDataUrl()
  return dataUrl && dataUrl.length > 0 ? dataUrl : resolveFormPdfLogoUrl()
}

const REPORTS_PDF_SCROLL_BG = '#eff1f3'

function clearPlaceholderHintsInClone(clonedElement: HTMLElement): void {
  clonedElement
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    .forEach((field) => {
      field.placeholder = ''
    })
}

function expandClonedNodeForFullCanvasCapture(
  clonedElement: HTMLElement,
): void {
  clonedElement.style.overflow = 'visible'
  clonedElement.style.maxHeight = 'none'
  clonedElement.style.height = 'auto'
  const scrollers = clonedElement.querySelectorAll<HTMLElement>(
    '.reports-preview-scroll',
  )
  scrollers.forEach((n) => {
    n.style.overflow = 'visible'
    n.style.maxHeight = 'none'
  })
}

function applyReportPdfExportStylesInClone(clonedElement: HTMLElement): void {
  const scrollEls: HTMLElement[] = []
  if (clonedElement.classList.contains('reports-preview-scroll')) {
    scrollEls.push(clonedElement)
  }
  clonedElement
    .querySelectorAll<HTMLElement>('.reports-preview-scroll')
    .forEach((n) => {
      if (n !== clonedElement) {
        scrollEls.push(n)
      }
    })
  for (const el of scrollEls) {
    el.style.background = REPORTS_PDF_SCROLL_BG
    el.style.backgroundColor = REPORTS_PDF_SCROLL_BG
  }

  clonedElement
    .querySelectorAll<HTMLElement>('.reports-preview-table-scroll')
    .forEach((el) => {
      el.style.overflow = 'visible'
      el.style.maxWidth = '100%'
      el.style.width = '100%'
    })

  clonedElement
    .querySelectorAll<HTMLElement>('.reports-preview-table')
    .forEach((el) => {
      el.style.width = '100%'
      el.style.minWidth = '100%'
      el.style.maxWidth = '100%'
    })

  clonedElement
    .querySelectorAll<HTMLElement>('.reports-preview-form__tables')
    .forEach((el) => {
      el.style.display = 'flex'
      el.style.flexDirection = 'column'
      el.style.flexWrap = 'nowrap'
      el.style.alignItems = 'stretch'
      el.style.gridTemplateColumns = 'none'
    })

  clonedElement
    .querySelectorAll<HTMLElement>('.reports-preview-table-block')
    .forEach((el) => {
      el.style.width = '100%'
      el.style.maxWidth = '100%'
      el.style.minWidth = '0'
      el.style.flex = '0 0 auto'
      el.style.gridColumn = 'auto'
    })
}

function resolveHtml2CanvasScale(element: HTMLElement, preferred = 2): number {
  const w = element.scrollWidth
  const h = element.scrollHeight
  if (w <= 0 || h <= 0) {
    return preferred
  }
  const maxCssEdge = Math.max(w, h)
  const atPreferred = maxCssEdge * preferred
  const maxEdge = 16384
  if (atPreferred <= maxEdge) {
    return preferred
  }
  return Math.max(1, maxEdge / maxCssEdge)
}

function injectFormPdfKioskHeaderIntoClone(
  clonedElement: HTMLElement,
  clonedDoc: Document,
  payload: FormPdfClinicHeaderPayload,
  logoSrc: string,
): void {
  const header = clonedDoc.createElement('div')
  header.className = 'form-pdf-kiosk-header'

  const img = clonedDoc.createElement('img')
  img.src = logoSrc
  img.alt = 'EnamelAI logo'
  img.className = 'form-pdf-kiosk-header-logo'

  const textWrap = clonedDoc.createElement('div')
  textWrap.className = 'form-pdf-kiosk-header-text'

  const nameEl = clonedDoc.createElement('div')
  nameEl.className = 'form-pdf-kiosk-header-clinic-name'
  nameEl.textContent = payload.clinicName
  textWrap.appendChild(nameEl)

  const addr = payload.clinicAddress?.trim() ?? ''
  if (addr.length > 0) {
    const addrEl = clonedDoc.createElement('div')
    addrEl.className = 'form-pdf-kiosk-header-clinic-address'
    addrEl.textContent = addr
    textWrap.appendChild(addrEl)
  }

  header.appendChild(img)
  header.appendChild(textWrap)
  clonedElement.insertBefore(header, clonedElement.firstChild)
}

interface FormPDFOptions {
  element: HTMLElement
  filename?: string
  title?: string
  clinicPdfHeader?: FormPdfClinicHeaderPayload
  onSuccess?: () => void
  onError?: (error: string) => void
}

export const generateFormPDF = async (
  options: FormPDFOptions,
): Promise<void> => {
  const {
    element,
    filename = `form-${new Date().toISOString().split('T')[0]}.pdf`,
    title,
    clinicPdfHeader,
    onSuccess,
    onError,
  } = options

  try {
    const logoSrcForClone = clinicPdfHeader
      ? await resolveFormPdfLogoSrcForCapture()
      : ''
    const captureHeight = clinicPdfHeader
      ? element.scrollHeight + FORM_PDF_HEADER_CAPTURE_EXTRA_HEIGHT
      : element.scrollHeight

    const scale = resolveHtml2CanvasScale(element, 2)
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale,
      useCORS: true,
      logging: false,
      width: element.scrollWidth,
      height: captureHeight,
      onclone: (clonedDoc, clonedElement) => {
        clearPlaceholderHintsInClone(clonedElement)
        expandClonedNodeForFullCanvasCapture(clonedElement)
        applyReportPdfExportStylesInClone(clonedElement)
        if (clinicPdfHeader) {
          injectFormPdfKioskHeaderIntoClone(
            clonedElement,
            clonedDoc,
            clinicPdfHeader,
            logoSrcForClone,
          )
        }
      },
    })

    const imgProps = {
      width: canvas.width,
      height: canvas.height,
    }
    if (imgProps.width <= 0 || imgProps.height <= 0) {
      throw new Error('Invalid canvas size for PDF generation.')
    }

    const pdf = new jsPDF('p', 'pt', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 40

    const imgWidth = pageWidth - margin * 2
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width

    const shouldRenderTopTitle = Boolean(title) && !clinicPdfHeader
    let yPos = margin
    if (shouldRenderTopTitle && title) {
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(18)
      pdf.text(title, margin, yPos)
      yPos += 30
    }

    let remainingHeight = imgHeight
    let sourceY = 0
    const sourceHeight = imgProps.height

    while (remainingHeight > 0) {
      let availableHeight = pageHeight - yPos - margin
      if (availableHeight <= 0) {
        pdf.addPage()
        yPos = margin
        availableHeight = pageHeight - yPos - margin
      }
      const heightToAdd = Math.min(remainingHeight, availableHeight)
      if (heightToAdd <= 0) {
        pdf.addPage()
        yPos = margin
        continue
        if (availableHeight <= 0) {
          pdf.addPage()
          yPos = margin
          continue
        }
        break
      }
      const sourceHeightToUse = (heightToAdd / imgHeight) * sourceHeight
      if (sourceHeightToUse <= 0) {
        break
      }

      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = sourceHeightToUse
      const ctx = pageCanvas.getContext('2d')

      if (ctx) {
        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeightToUse,
          0,
          0,
          canvas.width,
          sourceHeightToUse,
        )
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 1.0)
        const pageImgHeight = (sourceHeightToUse * imgWidth) / canvas.width
        if (pageImgHeight <= 0 || !Number.isFinite(pageImgHeight)) {
          break
        }

        pdf.addImage(pageImgData, 'JPEG', margin, yPos, imgWidth, pageImgHeight)
        yPos += pageImgHeight
        sourceY += sourceHeightToUse
        remainingHeight -= heightToAdd
      } else {
        break
      }
    }

    pdf.save(filename)
    onSuccess?.()
  } catch (error) {
    logger.error('Error generating form PDF:', error)
    onError?.('Failed to generate PDF. Please try again.')
  }
}
